import {IconMenu} from "components/Icons/IconMenu";
import Menu from "components/Menu";
import {motion, useCycle} from "framer-motion";
import {toggleBodyClass} from "modules/utils/dom_helpers";
import {useAdobeDTM, useInertModal} from "modules/utils/hooks";
import React, {Fragment, useEffect, useRef} from "react";
import {useLocation, useParams} from "react-router";
import styled from "@emotion/styled";
import {Exist} from "components/Exist";

const Wrapper = styled(motion.div)`
	z-index: 99;
	margin-left: auto;

	.background {
		position: fixed;
		top: 0;
		right: 0;
		bottom: 0;
		width: 300px;
		background: rgba(9, 11, 15, 0.9);
		z-index: 10;
	}
`;

const ClickArea = styled.button`
	position: fixed;
	left: 0;
	top: 0;
	height: 100%;
	width: 100%;
	z-index: 98;
	opacity: 0.1;
	pointer-events: auto;
`;

const MenuToggleIcon = styled.button`
	z-index: 100;
	position: relative;
	cursor: pointer;
	pointer-events: auto;
	background: transparent;
	border: none;
	padding: 0;

	.fixed-insights-view & {
		opacity: 0;
		transition: all 0.3s ease;
		transition-delay: 0.1s;
		pointer-events: none;
	}
`;

const sidebar = {
	open: (height: number) => {
		const webTransition = {
			type: "spring",
			stiffness: 15,
			restDelta: 2,
		};
		const CLIP_PATH_WIDTH = 200;
		return {
			clipPath: `circle(${height * 2 + CLIP_PATH_WIDTH}px at 86% 48px)`,
			WebkitClipPath: `circle(${height * 2 + CLIP_PATH_WIDTH}px at 86% 48px)`,
			transition: webTransition,
		};
	},
	closed: {
		clipPath: "circle(0px at 86% 48px)",
		WebkitClipPath: "circle(0px at 86% 48px)",
		transition: {
			delay: 0.5,
			type: "spring",
			stiffness: 400,
			damping: 40,
		},
	},
};

export const MenuToggle: React.FC = () => {
	const {pathname} = useLocation();
	const {trackAction} = useAdobeDTM();
	const [isOpen, toggleOpen] = useCycle(false, true);
	const containerRef = useRef<HTMLDivElement>(null);
	const windowHeight = window.innerHeight;
	const {code = ""} = useParams<{code: string}>();
	const isJoinGroupPage = Boolean(code);

	useInertModal(containerRef, isOpen, "menu");

	useEffect(() => {
		toggleBodyClass("menu_static_scroll", isOpen);
	}, [isOpen]);

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			// Close the window by pressing the Esc-key
			if (e.code === 'Escape' && isOpen) {
				toggleOpen();
			}
		};

		window.document.addEventListener("keydown", handler);

		return () => {
			window.document.removeEventListener("keydown", handler);
		};
	}, [isOpen, toggleOpen]);

	 
	useEffect(() => {
		if (isOpen) {
			toggleOpen();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname]);

	const onOpen = () => {
		trackAction({
			"action.name": "mlb play : beat the streak : home : menu click",
			"page.name": "mlb play : beat the streak : home",
		});
		toggleOpen();
	};

	return (
		<Fragment>
			{isOpen && (
				<ClickArea
					onClick={(e) => {
						e.stopPropagation();
						e.preventDefault();
						toggleOpen();
					}}
				/>
			)}
			<Wrapper
				initial={false}
				animate={isOpen ? "open" : "closed"}
				ref={containerRef}
				layout
				inherit={false}
			>
				<motion.div
					className={"background"}
					transformTemplate={() => `translateX(0) translateY(0) translateZ(0)`}
					variants={sidebar}
					custom={windowHeight}
				/>
				<Exist when={!isJoinGroupPage}>
					<MenuToggleIcon onClick={onOpen} aria-haspopup={"menu"} aria-label={"Menu"}>
						<IconMenu />
					</MenuToggleIcon>
				</Exist>
				<Menu isOpen={isOpen} toggleOpen={onOpen} />
			</Wrapper>
		</Fragment>
	);
};

export default MenuToggle;
